import React, { useState } from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import { useNavigate } from '@tanstack/react-router';
import { FiMusic, FiUsers } from 'react-icons/fi';

type Role = 'creator' | 'fan' | null;

import { useAuth } from '../../hooks/useAuth';
import { useUpdateProfileMutation } from '../../api/auth';
import { signupStore } from '../../utils/signupStore';
import { AuthHeader } from './AuthHeader';

export const RoleSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [selectedRole, setSelectedRole] = useState<Role>(null);
  const [isLoading, setIsLoading] = useState(false);
  const profileMutation = useUpdateProfileMutation();

  const handleContinue = async () => {
    if (!selectedRole) return;

    // Signup flow: save role to session store and navigate forward (no API call yet)
    if (signupStore.isActive()) {
      signupStore.set({ role: selectedRole });
      navigate({ to: '/personal-information' });
      return;
    }

    // Post-login onboarding: update profile via API
    setIsLoading(true);
    try {
      // Assuming endpoint for updating profile
      await profileMutation.mutateAsync({ role: selectedRole });
      updateUser({ role: selectedRole });
      navigate({ to: '/personal-information' });
    } catch (error) {
      console.error('Failed to update role:', error);
      // Fallback: still advance so the user isn't stuck
      updateUser({ role: selectedRole });
      navigate({ to: '/personal-information' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full flex bg-[#030303] flex-col overflow-hidden font-['Inter'] selection:bg-blue-500/30">
      {/* Background Glow */}
      <div
        className="absolute pointer-events-none rounded-full"
        style={{
          width: '1308px',
          height: '449px',
          bottom: '-150px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(to right, #2F80ED, #942FED)',
          opacity: 0.5,
          filter: 'blur(260px)',
          zIndex: 0,
        }}
      />

      <AuthHeader />

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center w-full max-w-150 mx-auto px-6">
        {/* Back Button & Text Section */}
        <div className="w-full text-center flex flex-col items-center">
          <div className="w-full flex justify-start mb-6">
            <button
              onClick={() => window.history.back()}
              className="w-10 h-10 rounded-full bg-[#1a1a1a] hover:bg-[#2a2a2a] flex items-center justify-center transition-colors border border-white/5 cursor-pointer shadow-lg"
              aria-label="Go back"
            >
              <FiArrowLeft className="text-zinc-300" size={18} />
            </button>
          </div>

          <h1
            className="text-white text-[32px] md:text-[40px] font-medium leading-[1.1] mb-2"
            style={{ fontFamily: "'Funnel Display', sans-serif" }}
          >
            Who Are You in the Music
            <br />
            Scene?
          </h1>
          <p className="text-[#a1a1aa] text-[14px] md:text-[15px] mb-10 font-medium">
            Select your role to personalize your Musicade experience
          </p>

          <div className="w-full flex flex-col gap-5 max-w-125">
            {/* Creator Card */}
            <button
              onClick={() => setSelectedRole('creator')}
              className={`relative flex items-center w-full rounded-2xl p-4 text-left transition-all duration-300 overflow-hidden cursor-pointer ${
                selectedRole === 'creator' ? 'ring-2 ring-white scale-[1.02]' : 'hover:scale-[1.01]'
              }`}
              style={{ background: 'linear-gradient(to right, #942FED, #A855F7)' }}
            >
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center shrink-0 backdrop-blur-sm shadow-inner group-hover:bg-white/30 transition-colors">
                <FiMusic className="text-white" size={22} />
              </div>
              <div className="flex flex-col ml-4 mr-6 flex-1">
                <span
                  className="text-white text-[16px] font-bold mb-0.5"
                  style={{ fontFamily: "'Satoshi', sans-serif" }}
                >
                  Creator
                </span>
                <span className="text-white/80 text-[13px] leading-snug">
                  Showcase your talent, grow your audience, and collaborate with others
                </span>
              </div>
              <div className="w-5 h-5 rounded-full border-[1.5px] border-white shrink-0 flex items-center justify-center bg-transparent ml-auto transition-colors">
                {selectedRole === 'creator' && (
                  <div className="w-2.5 h-2.5 bg-white rounded-full animate-[scaleIn_0.2s_ease-out_forwards]" />
                )}
              </div>
            </button>

            {/* Fan Card */}
            <button
              onClick={() => setSelectedRole('fan')}
              className={`relative flex items-center w-full rounded-2xl p-4 text-left transition-all duration-300 overflow-hidden cursor-pointer ${
                selectedRole === 'fan' ? 'ring-2 ring-white scale-[1.02]' : 'hover:scale-[1.01]'
              }`}
              style={{ background: 'linear-gradient(to right, #2F80ED, #60A5FA)' }}
            >
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center shrink-0 backdrop-blur-sm shadow-inner group-hover:bg-white/30 transition-colors">
                <FiUsers className="text-white" size={22} />
              </div>
              <div className="flex flex-col ml-4 mr-6 flex-1">
                <span
                  className="text-white text-[16px] font-bold mb-0.5"
                  style={{ fontFamily: "'Satoshi', sans-serif" }}
                >
                  Fan
                </span>
                <span className="text-white/80 text-[13px] leading-snug">
                  Explore music, support creators, and catch live vibes near you.
                </span>
              </div>
              <div className="w-5 h-5 rounded-full border-[1.5px] border-white shrink-0 flex items-center justify-center bg-transparent ml-auto transition-colors">
                {selectedRole === 'fan' && (
                  <div className="w-2.5 h-2.5 bg-white rounded-full animate-[scaleIn_0.2s_ease-out_forwards]" />
                )}
              </div>
            </button>

            {/* Continue Button */}
            <button
              onClick={handleContinue}
              disabled={!selectedRole || isLoading}
              className={`mt-6 h-12 w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white font-medium text-[15px] rounded-xl transition-all shadow-lg shadow-blue-500/20 active:scale-[0.98] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#3B82F6] disabled:active:scale-100`}
            >
              {isLoading ? 'Saving...' : 'Continue'}
            </button>
          </div>
        </div>
      </div>

      {/* Add custom scale animation for the radio button */}
      <style>{`
        @keyframes scaleIn {
          from { transform: scale(0); }
          to { transform: scale(1); }
        }
      `}</style>
    </div>
  );
};
